import { memo, useEffect, useMemo } from 'react';
import { COURT_FINDER_PAGE } from 'constants/pages';
import { getVenuePrimarySport } from 'constants/venues';
import {
  SportsEnum,
  type VenuesBoolExp,
  useGetBookableCourtsByGeoLazyQuery,
} from 'types/generated/client';
import { milesToMeters } from 'utils/shared/geo/milesToMeters';
import { type MarketplaceLinkParams, marketplaceUrl } from 'utils/shared/url/marketplaceUrl';
import ArrowRight from 'svg/ArrowRight';
import CourtFlat from 'svg/CourtFlat';
import type { FindFilters } from 'screens/Find/filters';
import { searchCenterFor, toGeographyPoint } from 'screens/Find/geo';
import { useViewerCenter } from 'screens/Find/useViewerCenter';
import { HomeFilters } from 'screens/PlayerHomeV3/components/GetOnCourtSection/types';
import { chosenRadiusMiles } from 'screens/PlayerHomeV3/components/TopOpportunitiesSection/sheetFilters';
import { FEED_RADIUS_MILES } from 'screens/PlayerHomeV3/components/TopOpportunitiesSection/useTopOpportunities';
import { ButtonLink } from 'components/Button';
import CardsSection from 'components/CardSection';
import Skeleton from 'components/LoadingSkeleton';
import CardVenue from 'components/cards/CardVenue';
import classNames from 'styles/utils/classNames';

const CARD_COUNT = 3;

const MemoizedCardVenue = memo(CardVenue);

interface Props {
  filters: HomeFilters;
  sheetFilters?: FindFilters;
  marketplaceParams?: MarketplaceLinkParams;
}

const venueSportFilter = (sport: SportsEnum): VenuesBoolExp => {
  if (sport === SportsEnum.Tennis) return { hasTennis: { _eq: true } };
  if (sport === SportsEnum.Padel) return { hasPadel: { _eq: true } };
  return { hasPickleball: { _eq: true } };
};

export default function BookableCourtsSection({ filters, sheetFilters, marketplaceParams }: Props) {
  const [getBookableCourts, { data, loading }] = useGetBookableCourtsByGeoLazyQuery();

  const radiusMiles = sheetFilters ? chosenRadiusMiles(sheetFilters) : FEED_RADIUS_MILES;
  const viewerCenter = useViewerCenter();
  const searchCenter = useMemo(
    () => searchCenterFor(sheetFilters?.neighborhoods ?? [], viewerCenter),
    [sheetFilters?.neighborhoods, viewerCenter],
  );

  useEffect(() => {
    getBookableCourts({
      variables: {
        limit: CARD_COUNT,
        distance: milesToMeters(radiusMiles),
        from: toGeographyPoint(searchCenter),
        sportFilter: venueSportFilter(filters.sport),
      },
    });
  }, [searchCenter, filters.sport, radiusMiles, getBookableCourts]);

  const venues = data?.venues ?? [];
  const noData = !loading && venues.length === 0;

  const renderVenueCard = (venue: (typeof venues)[number]) => {
    const image = venue.images.find((img) => img.isVisible) ?? venue.images[0];

    return (
      <MemoizedCardVenue
        key={venue.id}
        id={venue.id}
        slug={venue.slug}
        imageUrl={image?.url}
        title={venue.title}
        addressString={venue.addressString}
        accessType={venue.accessType}
        totalCourtCount={venue.indoorCourtCount + venue.outdoorCourtCount}
        pickleballNets={venue.pickleballNets}
        primarySport={getVenuePrimarySport(venue)}
      />
    );
  };

  return (
    // @todo empty-state design pending
    <section
      className={classNames(
        'container mx-auto mt-[0.875rem] max-lg:pr-0 max-sm:-mt-ds-2xl sm:max-lg:-mt-12',
        noData && 'hidden',
      )}
    >
      <div className="my-ds-2xl h-px w-full bg-color-border-input-lightmode dark:bg-color-border-input-darkmode max-lg:mr-ds-lg lg:hidden" />
      <div className="flex items-center gap-ds-sm max-lg:pr-ds-lg">
        <CourtFlat className="h-12 w-12 shrink-0 p-1.5 text-brand-blue-500 max-lg:hidden" />
        <h2 className="max-lg:typography-informative-subheading-medium-highlight lg:typography-product-heading-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          Courts you can book
        </h2>
      </div>

      {/* Mobile peeks the next card; desktop keeps the three-up grid. */}
      <div className="mt-ds-lg lg:hidden">
        <CardsSection
          loading={loading}
          gap="0.75rem"
          scrollOffset={{ base: 180, sm: 0 }}
          visibleCards={{ base: 1, sm: 2 }}
          cardsWrapperClassName="-my-3 py-3"
          data={venues}
          renderCard={renderVenueCard}
        />
      </div>

      <div className="mt-ds-lg grid grid-cols-3 gap-ds-lg max-lg:hidden">
        {loading
          ? Array.from({ length: CARD_COUNT }).map((_, index) => (
              <Skeleton key={index} height="21.75rem" borderRadius="0.5rem" />
            ))
          : venues.map(renderVenueCard)}
      </div>

      <div className="mt-ds-xl flex justify-end max-lg:mt-ds-lg max-lg:pr-ds-lg">
        <ButtonLink
          href={marketplaceUrl(COURT_FINDER_PAGE, marketplaceParams)}
          variant="primary-light"
          size="md"
          isInline
          iconLeft={<CourtFlat className="h-4 w-4 shrink-0 text-brand-blue-500" />}
          iconRight={<ArrowRight className="h-4 w-4 shrink-0" />}
          className="shadow-[0_4px_20px_0_rgba(0,0,0,0.16)]"
        >
          View all courts
        </ButtonLink>
      </div>
    </section>
  );
}
